import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut,
  Search,
  Filter,
  FileSpreadsheet,
  FileText,
  Trash2,
  Send,
  CheckCircle2,
  MessageSquare,
  X,
  Users,
  Apple,
  Smartphone,
  Layers,
  MailCheck,
} from "lucide-react";
import BarkLogo from "../components/BarkLogo";
import { api, API_BASE, clearToken, getToken, formatApiErrorDetail } from "../lib/api";

const STATUS_STYLES = {
  New: "bg-blue-50 text-blue-700",
  Invited: "bg-amber-50 text-amber-700",
  Installed: "bg-emerald-50 text-emerald-700",
  "Active Tester": "bg-teal-50 text-teal-700",
  "Feedback Received": "bg-fuchsia-50 text-fuchsia-700",
  "Not Selected": "bg-gray-100 text-gray-600",
  Removed: "bg-red-50 text-red-700",
};

function StatCard({ icon: Icon, label, value, testId, tint = "teal" }) {
  const tintCls = {
    teal: "bg-bark-teal-soft text-bark-teal",
    ios: "bg-slate-100 text-slate-700",
    android: "bg-emerald-50 text-emerald-700",
    both: "bg-indigo-50 text-indigo-700",
  }[tint];
  return (
    <div className="bg-white rounded-2xl p-5 shadow-bark-soft" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl grid place-items-center ${tintCls}`}>
          <Icon size={18} />
        </div>
        <div>
          <div className="text-[12px] uppercase tracking-widest font-bold text-bark-muted">{label}</div>
          <div className="text-2xl font-black text-bark-ink">{value ?? 0}</div>
        </div>
      </div>
    </div>
  );
}

function ExportButton({ href, filename, label, icon: Icon, variant = "primary", testId }) {
  const [busy, setBusy] = React.useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      const res = await fetch(href, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Export failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };
  const base =
    "inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-[13px] font-bold transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed";
  const styles =
    variant === "primary"
      ? "bg-bark-teal text-white shadow-bark-soft hover:bg-bark-teal-hover"
      : "bg-white text-bark-ink border border-bark-teal/25 hover:border-bark-teal";
  return (
    <button onClick={onClick} disabled={busy} className={`${base} ${styles}`} data-testid={testId}>
      <Icon size={14} />
      {busy ? "Preparing…" : label}
    </button>
  );
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = React.useState({ items: [], stats: {}, status_options: [], platform_options: [] });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [platform, setPlatform] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [q, setQ] = React.useState("");
  const [editingNote, setEditingNote] = React.useState(null); // {id, text}
  const [notifStatus, setNotifStatus] = React.useState(null);

  const loadNotifStatus = React.useCallback(async () => {
    try {
      const { data } = await api.get("/admin/notifications/status");
      setNotifStatus(data);
    } catch (e) {
      // silent
    }
  }, []);

  React.useEffect(() => {
    document.title = "Beta Signups | Bark Buddies Admin";
    if (!getToken()) {
      navigate("/admin/login", { replace: true });
    }
  }, [navigate]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (platform) params.platform = platform;
      if (status) params.status = status;
      if (q) params.q = q;
      const { data } = await api.get("/admin/beta-signups", { params });
      setData(data);
    } catch (err) {
      if (err.response?.status === 401) {
        clearToken();
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [platform, status, q, navigate]);

  React.useEffect(() => {
    load();
    loadNotifStatus();
  }, [load, loadNotifStatus]);

  const stats = data.stats || {};
  const items = data.items || [];

  const logout = () => {
    clearToken();
    navigate("/admin/login", { replace: true });
  };

  const updateSignup = async (id, patch) => {
    try {
      const { data: r } = await api.patch(`/admin/beta-signups/${id}`, patch);
      setData((d) => ({
        ...d,
        items: d.items.map((it) => (it.id === id ? r.signup : it)),
      }));
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const deleteSignup = async (id) => {
    if (!window.confirm("Delete this signup permanently?")) return;
    try {
      await api.delete(`/admin/beta-signups/${id}`);
      setData((d) => ({ ...d, items: d.items.filter((it) => it.id !== id) }));
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const openNote = (row) => setEditingNote({ id: row.id, text: row.notes || "" });
  const saveNote = async () => {
    if (!editingNote) return;
    await updateSignup(editingNote.id, { notes: editingNote.text });
    setEditingNote(null);
  };

  const [sendingTest, setSendingTest] = React.useState(false);
  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      const { data } = await api.post("/admin/notifications/test");
      alert(data.message || "Test email queued.");
      loadNotifStatus();
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSendingTest(false);
    }
  };

  const [runningDigest, setRunningDigest] = React.useState(false);
  const runDigestNow = async () => {
    setRunningDigest(true);
    try {
      const { data } = await api.post("/admin/notifications/digest/run");
      if (data.sent === 0) {
        alert(data.message || "No un-notified signups — nothing to send.");
      } else {
        alert(`Digest sent to ${notifStatus?.notify_email || "beta inbox"} covering ${data.sent} signup${data.sent === 1 ? "" : "s"}.`);
      }
      loadNotifStatus();
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setRunningDigest(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
    } catch {
      return iso;
    }
  };

  const statusOptions = data.status_options?.length ? data.status_options : [
    "New","Invited","Installed","Active Tester","Feedback Received","Not Selected","Removed",
  ];

  return (
    <div className="min-h-screen bg-bark-admin" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200/60 sticky top-0 z-30">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="no-underline">
            <BarkLogo size={36} />
          </Link>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-[13px] text-bark-muted font-semibold">Admin</span>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2 text-[13px] font-bold text-bark-ink hover:border-bark-teal transition-colors"
              data-testid="admin-logout"
            >
              <LogOut size={14} />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[12px] font-bold uppercase tracking-widest text-bark-teal">Dashboard</div>
            <h1 className="mt-1 text-3xl md:text-4xl font-black tracking-tight text-bark-ink">
              Beta signups
            </h1>
            <p className="mt-1 text-[14px] text-bark-muted">
              Manage testers, update statuses, and export tester lists.
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats">
          <StatCard icon={Users} label="Total signups" value={stats.total} testId="stat-total" />
          <StatCard icon={Apple} label="iOS signups" value={stats.ios} tint="ios" testId="stat-ios" />
          <StatCard icon={Smartphone} label="Android signups" value={stats.android} tint="android" testId="stat-android" />
          <StatCard icon={Layers} label="Both-platform" value={stats.both} tint="both" testId="stat-both" />
          <StatCard icon={Filter} label="New signups" value={stats.new} testId="stat-new" />
          <StatCard icon={Send} label="Invited testers" value={stats.invited} testId="stat-invited" />
          <StatCard icon={CheckCircle2} label="Installed testers" value={stats.installed} testId="stat-installed" />
          <StatCard icon={MessageSquare} label="Feedback received" value={stats.feedback} testId="stat-feedback" />
        </div>

        {/* Exports */}
        <div className="mt-6 bg-white rounded-2xl p-5 shadow-bark-soft" data-testid="admin-exports">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex-1 min-w-[240px]">
              <div className="text-[13px] font-bold uppercase tracking-widest text-bark-muted">Exports & notifications</div>
              <div className="text-[15px] font-bold text-bark-ink mt-1">
                Download tester lists or manage the beta email notification pipeline.
              </div>
              {notifStatus && (
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-bark-muted" data-testid="notif-status">
                  <span>
                    Mode: <b className="text-bark-ink">{notifStatus.mode}</b>
                    {notifStatus.mode === "auto" && (
                      <> → effective <b className="text-bark-ink">{notifStatus.effective_mode}</b></>
                    )}
                  </span>
                  <span>Signups last hour: <b className="text-bark-ink">{notifStatus.signups_last_hour}</b></span>
                  <span>Auto threshold: <b className="text-bark-ink">{notifStatus.auto_threshold}/h</b></span>
                  <span>Digest every <b className="text-bark-ink">{notifStatus.digest_interval_minutes}min</b></span>
                  <span>Pending digest: <b className="text-bark-ink">{notifStatus.pending_digest_count}</b></span>
                  {notifStatus.last_digest_run_at && (
                    <span>Last digest: <b className="text-bark-ink">{new Date(notifStatus.last_digest_run_at).toLocaleString()}</b> ({notifStatus.last_digest_sent_count} sent)</span>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={sendTestEmail}
                disabled={sendingTest}
                className="inline-flex items-center gap-2 rounded-full bg-white border border-bark-teal/25 hover:border-bark-teal text-bark-ink px-4 py-2.5 text-[13px] font-bold transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="admin-send-test-email"
              >
                <MailCheck size={14} />
                {sendingTest ? "Sending…" : "Send test email"}
              </button>
              <button
                onClick={runDigestNow}
                disabled={runningDigest}
                className="inline-flex items-center gap-2 rounded-full bg-bark-teal text-white hover:bg-bark-teal-hover px-4 py-2.5 text-[13px] font-bold transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="admin-run-digest"
              >
                <Send size={14} />
                {runningDigest ? "Sending…" : "Run digest now"}
              </button>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-[12px] font-bold uppercase tracking-widest text-bark-muted mb-2">Excel (.xlsx)</div>
              <div className="flex flex-wrap gap-2">
                <ExportButton
                  testId="export-xlsx-all"
                  href={`${API_BASE}/admin/beta-signups/export/all.xlsx`}
                  filename="bark_buddies_all_beta_signups.xlsx"
                  label="Export All Signups Excel"
                  icon={FileSpreadsheet}
                />
                <ExportButton
                  testId="export-xlsx-ios"
                  href={`${API_BASE}/admin/beta-signups/export/ios.xlsx`}
                  filename="bark_buddies_ios_beta_testers.xlsx"
                  label="Export iOS Testers Excel"
                  icon={FileSpreadsheet}
                  variant="outline"
                />
                <ExportButton
                  testId="export-xlsx-android"
                  href={`${API_BASE}/admin/beta-signups/export/android.xlsx`}
                  filename="bark_buddies_android_beta_testers.xlsx"
                  label="Export Android Testers Excel"
                  icon={FileSpreadsheet}
                  variant="outline"
                />
              </div>
            </div>
            <div>
              <div className="text-[12px] font-bold uppercase tracking-widest text-bark-muted mb-2">CSV</div>
              <div className="flex flex-wrap gap-2">
                <ExportButton
                  testId="export-csv-all"
                  href={`${API_BASE}/admin/beta-signups/export/all.csv`}
                  filename="bark_buddies_all_beta_signups.csv"
                  label="Export All Signups CSV"
                  icon={FileText}
                  variant="outline"
                />
                <ExportButton
                  testId="export-csv-ios"
                  href={`${API_BASE}/admin/beta-signups/export/ios.csv`}
                  filename="bark_buddies_ios_beta_testers.csv"
                  label="Export iOS Testers CSV"
                  icon={FileText}
                  variant="outline"
                />
                <ExportButton
                  testId="export-csv-android"
                  href={`${API_BASE}/admin/beta-signups/export/android.csv`}
                  filename="bark_buddies_android_beta_testers.csv"
                  label="Export Android Testers CSV"
                  icon={FileText}
                  variant="outline"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 bg-white rounded-2xl p-4 md:p-5 shadow-bark-soft flex flex-wrap items-center gap-3" data-testid="admin-filters">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-bark-muted" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or email"
              className="w-full pl-9 pr-3 py-2.5 rounded-full border border-slate-200 bg-slate-50 text-[14px] focus:border-bark-teal focus:bg-white focus:outline-none"
              data-testid="admin-search"
            />
          </div>
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2.5 text-[14px] font-semibold text-bark-ink focus:border-bark-teal focus:bg-white focus:outline-none"
            data-testid="admin-filter-platform"
          >
            <option value="">All platforms</option>
            <option value="iOS">iOS</option>
            <option value="Android">Android</option>
            <option value="Both">Both</option>
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2.5 text-[14px] font-semibold text-bark-ink focus:border-bark-teal focus:bg-white focus:outline-none"
            data-testid="admin-filter-status"
          >
            <option value="">All statuses</option>
            {statusOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          {(q || platform || status) && (
            <button
              onClick={() => { setQ(""); setPlatform(""); setStatus(""); }}
              className="text-[13px] font-bold text-bark-muted hover:text-bark-ink inline-flex items-center gap-1"
              data-testid="admin-filter-clear"
            >
              <X size={14} /> Clear
            </button>
          )}
        </div>

        {/* Table */}
        <div className="mt-4 bg-white rounded-2xl shadow-bark-soft overflow-hidden" data-testid="admin-table-wrapper">
          {error && (
            <div className="p-4 text-[14px] text-red-700 bg-red-50 border-b border-red-200">
              {error}
            </div>
          )}
          {loading ? (
            <div className="p-10 text-center text-bark-muted" data-testid="admin-loading">Loading signups…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center" data-testid="admin-empty">
              <div className="text-[15px] font-bold text-bark-ink">No signups yet</div>
              <div className="mt-1 text-[13px] text-bark-muted">
                When people join the Android beta, they'll show up here.
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]" data-testid="admin-table">
                <thead className="bg-slate-50 text-bark-muted text-[11px] uppercase tracking-widest">
                  <tr>
                    <Th>Signup date</Th>
                    <Th>Status</Th>
                    <Th>Name</Th>
                    <Th>Email</Th>
                    <Th>Phone</Th>
                    <Th>City / State</Th>
                    <Th>Platform</Th>
                    <Th>Device / OS</Th>
                    <Th>Dog</Th>
                    <Th>Test interests</Th>
                    <Th>Consent</Th>
                    <Th>Invite sent</Th>
                    <Th>Installed</Th>
                    <Th>Feedback</Th>
                    <Th>Notes</Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50/60 align-top" data-testid={`row-${row.id}`}>
                      <Td>{fmtDate(row.created_at)}</Td>
                      <Td>
                        <select
                          value={row.status}
                          onChange={(e) => updateSignup(row.id, { status: e.target.value })}
                          className={`rounded-full px-3 py-1 text-[12px] font-bold border border-transparent focus:border-bark-teal focus:outline-none ${
                            STATUS_STYLES[row.status] || "bg-slate-100 text-slate-700"
                          }`}
                          data-testid={`status-select-${row.id}`}
                        >
                          {statusOptions.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </Td>
                      <Td>
                        <div className="font-bold text-bark-ink">{row.first_name} {row.last_name}</div>
                      </Td>
                      <Td>
                        <a href={`mailto:${row.email}`} className="bark-link">{row.email}</a>
                      </Td>
                      <Td>{row.phone || "—"}</Td>
                      <Td>{row.city}{row.state ? `, ${row.state}` : ""}</Td>
                      <Td>
                        <span className="rounded-full bg-bark-teal-soft text-bark-teal px-2.5 py-0.5 text-[12px] font-bold">
                          {row.platform}
                        </span>
                      </Td>
                      <Td>
                        <div className="text-bark-ink">{row.device_model}</div>
                        <div className="text-bark-muted">{row.os_version}</div>
                      </Td>
                      <Td>
                        {row.owns_dog ? (
                          <div>
                            <div>{row.dog_count || 1} dog{(row.dog_count || 1) > 1 ? "s" : ""}</div>
                            {row.dog_size && <div className="text-bark-muted">{row.dog_size}</div>}
                          </div>
                        ) : (
                          <span className="text-bark-muted">No</span>
                        )}
                      </Td>
                      <Td>
                        {(row.test_interests || []).length ? (
                          <div className="max-w-[220px] text-bark-muted line-clamp-3">
                            {row.test_interests.join(", ")}
                          </div>
                        ) : "—"}
                      </Td>
                      <Td>
                        <div className={row.consent_to_contact ? "text-emerald-700 font-bold" : "text-red-700 font-bold"}>
                          {row.consent_to_contact ? "Yes" : "No"}
                        </div>
                        <div className="text-[11px] text-bark-muted">Terms: {row.accepted_privacy_terms ? "Yes" : "No"}</div>
                      </Td>
                      <Td>{fmtDate(row.invite_sent_date)}</Td>
                      <Td>
                        <button
                          onClick={() => updateSignup(row.id, { installed: !row.installed })}
                          className={`rounded-full px-3 py-1 text-[12px] font-bold ${
                            row.installed ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                          }`}
                          data-testid={`toggle-installed-${row.id}`}
                        >
                          {row.installed ? "Yes" : "No"}
                        </button>
                      </Td>
                      <Td>
                        <button
                          onClick={() => updateSignup(row.id, { feedback_received: !row.feedback_received })}
                          className={`rounded-full px-3 py-1 text-[12px] font-bold ${
                            row.feedback_received ? "bg-fuchsia-50 text-fuchsia-700" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                          }`}
                          data-testid={`toggle-feedback-${row.id}`}
                        >
                          {row.feedback_received ? "Yes" : "No"}
                        </button>
                      </Td>
                      <Td>
                        <button
                          onClick={() => openNote(row)}
                          className="max-w-[200px] text-left text-bark-muted hover:text-bark-ink"
                          data-testid={`open-note-${row.id}`}
                        >
                          {row.notes ? (
                            <span className="line-clamp-2">{row.notes}</span>
                          ) : (
                            <span className="italic">Add note</span>
                          )}
                        </button>
                      </Td>
                      <Td>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => updateSignup(row.id, { mark_invite_sent: true })}
                            className="rounded-full p-1.5 hover:bg-bark-teal-soft text-bark-teal"
                            title="Mark invite sent"
                            data-testid={`mark-invite-${row.id}`}
                          >
                            <Send size={14} />
                          </button>
                          <button
                            onClick={() => deleteSignup(row.id)}
                            className="rounded-full p-1.5 hover:bg-red-50 text-red-600"
                            title="Delete spam signup"
                            data-testid={`delete-${row.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editingNote && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="note-modal">
          <div className="bg-white rounded-3xl w-full max-w-lg p-6 shadow-bark-lift">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[13px] font-bold uppercase tracking-widest text-bark-teal">Notes</div>
              <button onClick={() => setEditingNote(null)} className="text-bark-muted hover:text-bark-ink" data-testid="note-cancel">
                <X size={18} />
              </button>
            </div>
            <textarea
              value={editingNote.text}
              onChange={(e) => setEditingNote((n) => ({ ...n, text: e.target.value }))}
              rows={5}
              className="w-full rounded-2xl border border-slate-200 p-3 text-[14px] focus:border-bark-teal focus:outline-none"
              placeholder="Add internal notes about this tester…"
              data-testid="note-textarea"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setEditingNote(null)}
                className="rounded-full px-4 py-2 text-[13px] font-bold text-bark-ink hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                onClick={saveNote}
                className="rounded-full bg-bark-teal px-5 py-2 text-[13px] font-bold text-white hover:bg-bark-teal-hover"
                data-testid="note-save"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Th = ({ children }) => (
  <th className="px-3 py-2.5 text-left font-bold whitespace-nowrap">{children}</th>
);
const Td = ({ children }) => (
  <td className="px-3 py-3 whitespace-nowrap text-bark-ink">{children}</td>
);
